import AddFormulations from "components/AddFormulations";
import { FrozenDataGrid } from "components/DataGrid";
import { useCsvToGridDataAsync } from "components/DataGrid/hooks";
import {
  ColumnExtraOptions,
  DataGridHandleWithData,
} from "components/DataGrid/types";
import { CsvValidationErrorContext } from "components/ValidationDashboard/contexts";
import { IRow } from "csv/types";
import { observer } from "mobx-react-lite";
import { useProject } from "pages/Workspace/hooks";
import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
} from "react";
import { RowsChangeData } from "react-data-grid";
import { useStore } from "store";
import { scaledRem } from "utils";
import FormulationEditor from "./FormulationEditor";
import FormulationFormatter from "./FormulationFormatter";

type Props = {
  editable: boolean;
  isSheetActive: boolean;
  onSheetActive: (sheet: string) => void;
};

const blankValidations = [
  "specs",
  "code",
  "plant",
  "clusters",
  "plantType",
  "countries",
  "packSizes",
];

const FormulationsTabHead: React.FC<Props> = ({
  editable,
  isSheetActive,
  onSheetActive,
}) => {
  const store = useStore();
  const project = useProject();
  const ref = useRef<DataGridHandleWithData>(null);
  const validationSummary = useContext(CsvValidationErrorContext);

  const schema = useCallback(
    (columnKey: string, sheetEditable?: boolean): ColumnExtraOptions<IRow> => {
      switch (columnKey) {
        case "id":
          return {
            editable: false,
            formatter: ({ rowIdx }) => <>{rowIdx + 1}</>,
            name: "",
            resizable: false,
            frozen: true,
          };
        case "rcode":
          return {
            editable: false,
            name: "",
            resizable: false,
            frozen: true,
            width: scaledRem(333),
            cellClass: "cell-readonly",
            colSpan() {
              return 3;
            },
          };
        case "description":
        case "crdStatus":
          return {
            frozen: true,
            minWidth: 0,
            maxWidth: 0,
            editable: false,
          };
      }
      return {
        editor: FormulationEditor,
        cellClass: () => {
          if (!sheetEditable) {
            return sheetEditable ?? true ? undefined : "cell-readonly";
          }
          const formulation = store.formulations.listItems.get(
            parseInt(columnKey, 10)
          );
          if (formulation) {
            return formulation.isCltLocked ? "cell-readonly" : undefined;
          }
          return undefined;
        },
        editable: () => {
          if (!sheetEditable) {
            return sheetEditable ?? true;
          }
          const formulation = store.formulations.listItems.get(
            parseInt(columnKey, 10)
          );
          if (formulation) {
            return !formulation.isCltLocked;
          }
          return false;
        },
        editorOptions: {
          editOnClick: false,
        },
        formatter: FormulationFormatter,
        name: "\xa0", // Insert non-breaking space so column select keeps working.
        resizable: false,
        width: scaledRem(123),
      };
    },
    [store.formulations.listItems]
  );

  const csvString = project ? store.formulations.headAsCsv : "";
  const [columns, rows, loaded] = useCsvToGridDataAsync({
    csvString,
    editable,
    schema,
  });

  const headErrors = useMemo(() => {
    const errors: Record<string, string> = {};
    rows.forEach((item) => {
      if (blankValidations.includes(`${item.id}`)) {
        columns
          .filter((e) => e.key !== "crdStatus" && e.key !== "description")
          .forEach((columnItem) => {
            if (item[columnItem.key] === "") {
              const cellKey = `${columnItem.key}-${item.id}`;
              errors[cellKey] = "E:This field cannot be left blank";
            }
          });
      }
    });
    return errors;
  }, [columns, rows]);

  useEffect(() => {
    const newErrorKeys = new Set<string>();
    const newColumnNameMap: Record<string, string> = {};

    rows.forEach((item) => {
      if (blankValidations.includes(`${item.id}`)) {
        columns
          .filter((e) => e.key !== "crdStatus" && e.key !== "description")
          .forEach((columnItem) => {
            if (item[columnItem.key] === "") {
              newErrorKeys.add(`${item.id}-E002`);
              newColumnNameMap[item.id] = `${item.rcode}`;
            }
          });
      }
    });

    const headErrorSize = Object.keys(headErrors).length;

    validationSummary.setters.setErrorCount((prev) => ({
      ...prev,
      head: headErrorSize,
    }));
    validationSummary.setters.setErrorKeys((prev) => ({
      ...prev,
      head: newErrorKeys,
    }));
    validationSummary.setters.setColumnNameMaps((prev) => ({
      ...prev,
      head: newColumnNameMap,
    }));
  }, [columns, headErrors, rows, validationSummary.setters]);

  const handleAddFormula = useCallback(
    (addCount: number) => {
      if (!project || !project.id) {
        return;
      }
      for (let i = 0; i < addCount; i++) {
        store.formulations.create(project.id);
      }
      store.formulations.commitCsv(project.id);
    },
    [project, store.formulations]
  );

  const handleColumnsReorder = useCallback(
    (sourceKey: string, targetKey: string) => {
      if (!project) {
        return;
      }
      store.formulations.reorder(
        project.id,
        parseInt(sourceKey, 10),
        parseInt(targetKey, 10)
      );
      store.formulations.commitCsv(project.id);
    },
    [project, store.formulations]
  );

  const handleHeadRowsChange = useCallback(
    (rows: IRow[], data?: RowsChangeData<IRow, unknown>) => {
      if (!project) {
        return;
      }
      if (!data) {
        return;
      }

      const { key } = data.column; // key = id of formulation
      const { indexes } = data;

      indexes.forEach((index) => {
        const { id } = rows[index]; // id = field name
        const formulation = store.formulations.listItems.get(parseInt(key, 10));
        formulation?.setValue(id as string, rows[index][key]);
      });

      store.formulations.commitHeadCsv(project.id);
      project.setDirty();
    },
    [project, store.formulations]
  );

  if (!loaded) {
    return null;
  }

  return (
    <FrozenDataGrid
      ref={ref}
      id="formulations"
      isActive={isSheetActive}
      multiple
      title="Formulations"
      titleExtra={<AddFormulations onAddFormulations={handleAddFormula} />}
      defaultCollapsed
      defaultColumnOptions={{ resizable: false }}
      rows={rows}
      minRowsShown={6}
      maxRowsShown="auto"
      columns={columns}
      onActive={onSheetActive}
      onColumnsReorder={handleColumnsReorder}
      onRowsChange={handleHeadRowsChange}
      rowIdentifier="id"
      errors={headErrors}
    />
  );
};

export default observer(FormulationsTabHead);
